import { Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";

import { RectConfig } from "konva/lib/shapes/Rect";
import { LineConfig } from "konva/lib/shapes/Line";
import { ArrowConfig } from "konva/lib/shapes/Arrow";
import { TextConfig } from "konva/lib/shapes/Text";
import { map } from "rxjs/operators";

export const typeConfigKonva = {
  RECT: "rect",
  PENCIL: "pencil",
  LINE: "line",
  ARROW: "arrow",
  TEXT: "text",
  CIRCLE: "circle",
} as const;

export type TypeConfigKonva =
  (typeof typeConfigKonva)[keyof typeof typeConfigKonva];

export interface SelectedKonvaElement {
  name: string;
  type: TypeConfigKonva;
}

@Injectable({
  providedIn: "root",
})
export class CanvasService {
  public selectedElement$ = new BehaviorSubject<SelectedKonvaElement>(null);
  public rectangles$ = new BehaviorSubject<RectConfig[]>([]);
  public lines$ = new BehaviorSubject<LineConfig[]>([]);
  public arrows$ = new BehaviorSubject<ArrowConfig[]>([]);
  public texts$ = new BehaviorSubject<TextConfig[]>([]);
  public mode$ = new BehaviorSubject<TypeConfigKonva | null>(null);
  private isLoadedImage$ = new BehaviorSubject<boolean>(false);
  private isErrorLoadedImage$ = new BehaviorSubject<boolean>(false);
  private editImage$ = new BehaviorSubject<File>(null);

  private readonly DEFAULT_COLOR = "#ff0000";
  public color$ = new BehaviorSubject<string>(this.DEFAULT_COLOR);
  private readonly DEFAULT_WIDTH = 3;
  public width$ = new BehaviorSubject<number>(this.DEFAULT_WIDTH);
  private readonly BASE_FONT_SIZE = 14;
  private configArrow: ArrowConfig = {
    x: 0,
    y: 0,
    points: [0, 0, 0, 0],
    pointerLength: 10,
    pointerWidth: 8,
    fill: this.color$.getValue(),
    stroke: this.color$.getValue(),
    strokeWidth: this.width$.getValue(),
    draggable: true,
    type: typeConfigKonva.ARROW,
  };

  private configRect: RectConfig = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    stroke: this.color$.getValue(),
    strokeWidth: this.width$.getValue(),
    draggable: true,
    type: typeConfigKonva.RECT,
  };

  private configText: TextConfig = {
    x: 20,
    y: 15,
    text: "",
    fontSize: this.width$.getValue() * this.BASE_FONT_SIZE,
    fontFamily: "Arial",
    fill: this.color$.getValue(),
    draggable: true,
    type: typeConfigKonva.TEXT,
  };

  private configLine: LineConfig = {
    points: [],
    stroke: this.color$.getValue(),
    strokeWidth: this.width$.getValue(),
    lineCap: "round",
    lineJoin: "round",
    tension: 1,
    draggable: true,
    type: typeConfigKonva.LINE,
  };

  public getCheckChangesHaveBeenMade(): number {
    return (
      this.lines$.getValue().length ||
      this.arrows$.getValue().length ||
      this.rectangles$.getValue().length ||
      this.texts$.getValue().length
    );
  }

  public getStateIsChanges$(): Observable<boolean> {
    return combineLatest([
      this.rectangles$,
      this.lines$,
      this.arrows$,
      this.texts$,
    ]).pipe(
      map(([rectangles, lines, arrows, texts]) => {
        return (
          rectangles.length === 0 &&
          lines.length === 0 &&
          arrows.length === 0 &&
          texts.length === 0
        );
      })
    );
  }

  public setIsLoadedImage(isLoaded: boolean): void {
    this.isLoadedImage$.next(isLoaded);
  }

  public getIsLoadedImage$(): Observable<boolean> {
    return this.isLoadedImage$;
  }

  public setErrorIsLoadedImage(isLoaded: boolean): void {
    this.isErrorLoadedImage$.next(isLoaded);
  }

  public getErrorIsLoadedImage$(): Observable<boolean> {
    return this.isErrorLoadedImage$;
  }

  public setMode(mode: TypeConfigKonva): void {
    this.mode$.next(mode);
  }

  public setColor(color: string): void {
    this.color$.next(color);
  }

  public setSelectedElement(element: SelectedKonvaElement): void {
    this.selectedElement$.next(element);
  }

  public removeSelectedElement(): void {
    this.selectedElement$.next(null);
  }

  public setWidth(width: number): void {
    this.width$.next(width);
  }

  public addRectangle({
    name,
    x,
    y,
  }: {
    name: string;
    x: number;
    y: number;
  }): void {
    const newRect = {
      ...this.configRect,
      x,
      y,
      stroke: this.color$.getValue(),
      strokeWidth: this.width$.getValue(),
      name,
    };

    const currentRectangles = this.rectangles$.getValue();
    currentRectangles.push(newRect);
    this.rectangles$.next(currentRectangles);
  }

  public updateRectangle({
    name,
    x,
    y,
  }: {
    name: string;
    x: number;
    y: number;
  }) {
    const current = this.rectangles$.getValue();

    const updatedRect = current.map((rect) => {
      if (rect.name === name) {
        return {
          ...rect,
          width: x - rect.x,
          height: y - rect.y,
        };
      }
      return rect;
    });

    this.rectangles$.next(updatedRect);
  }

  public deleteRectangle(idToDelete: string): void {
    const current = this.rectangles$.getValue();
    const updated = current.filter((rect) => rect.name !== idToDelete);
    this.rectangles$.next(updated);
  }

  public addLines({
    name,
    x,
    y,
  }: {
    name: string;
    x: number;
    y: number;
  }): void {
    const newLine = {
      ...this.configLine,
      stroke: this.color$.getValue(),
      strokeWidth: this.width$.getValue(),
      points: new Float64Array([x, y]),
      name,
    };
    const currentLines = this.lines$.getValue();
    currentLines.push(newLine);
    this.lines$.next(currentLines);
  }

  public updateContinuousLinePoints({
    name,
    x,
    y,
  }: {
    name: string;
    x: number;
    y: number;
  }) {
    const current = this.lines$.getValue();

    const updatedLines = current.map((line) => {
      if (line.name === name) {
        // 1. Создаем новый Float64Array нужной длины
        const oldPoints = line.points;
        const newPoints = new Float64Array(oldPoints.length + 2);

        // 2. Копируем старые значения. Это быстрая, низкоуровневая операция.
        newPoints.set(oldPoints);

        // 3. Добавляем новые точки в конец
        newPoints[oldPoints.length] = x;
        newPoints[oldPoints.length + 1] = y;

        return {
          ...line,
          points: newPoints, // <-- Присваиваем новый Float64Array
        };
      }
      return line;
    });

    this.lines$.next(updatedLines);
  }

  public updateLinePoints({
    name,
    x,
    y,
  }: {
    name: string;
    x: number;
    y: number;
  }) {
    const current = this.lines$.getValue();

    const updatedLines = current.map((line) => {
      if (line.name === name) {
        const currentPoints = line.points;
        let newPoints: Float64Array;

        // --- Ключевая логика ---
        if (currentPoints.length === 2) {
          // У линии только начальная точка. Добавляем конечную.
          // Создаем новый массив размером 4 и копируем в него старые точки + новые.
          newPoints = new Float64Array(4);
          newPoints.set(currentPoints, 0); // Копируем [x1, y1]
          newPoints[2] = x; // Устанавливаем x2
          newPoints[3] = y; // Устанавливаем y2
        } else {
          // У линии уже есть две или более точек.
          // Мы просто обновляем координаты последней точки.
          // Создаем точную копию текущего массива.
          newPoints = new Float64Array(currentPoints);
          // Обновляем последние два элемента (координаты конечной точки)
          newPoints[currentPoints.length - 2] = x;
          newPoints[currentPoints.length - 1] = y;
        }

        return {
          ...line,
          points: newPoints,
        };
      }
      return line;
    });
    this.lines$.next(updatedLines);
  }

  public deleteLine(idToDelete: string): void {
    const current = this.lines$.getValue();
    const updated = current.filter((line) => line.name !== idToDelete);
    this.lines$.next(updated);
  }

  public addArrow({
    name,
    x,
    y,
  }: {
    name: string;
    x: number;
    y: number;
  }): void {
    const newArrow = {
      ...this.configArrow,
      x,
      y,
      stroke: this.color$.getValue(),
      strokeWidth: this.width$.getValue(),
      fill: this.color$.getValue(),
      name,
    };
    const current = this.arrows$.getValue();
    this.arrows$.next([...current, newArrow]);
  }

  public updateArrowPoints({
    name,
    x,
    y,
  }: {
    name: string;
    x: number;
    y: number;
  }) {
    const current = this.arrows$.getValue();

    const updatedArrows = current.map((arrow) => {
      if (arrow.name === name) {
        const newX = x - arrow.x;
        const newY = y - arrow.y;
        const newPoints = [0, 0, newX, newY];

        return {
          ...arrow,
          points: newPoints,
        };
      }
      return arrow;
    });

    this.arrows$.next(updatedArrows);
  }

  public deleteArrow(idToDelete: string): void {
    const current = this.arrows$.getValue();
    const updated = current.filter((arrow) => arrow.name !== idToDelete);
    this.arrows$.next(updated);
  }

  public addText(text: string): void {
    const newText = {
      ...this.configText,
      fill: this.color$.getValue(),
      fontSize: this.width$.getValue() * this.BASE_FONT_SIZE,
      text,
      name: typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
    };

    const current = this.texts$.getValue();
    this.texts$.next([...current, newText]);
  }

  public updateText({ name, text }: { name: string; text: string }): void {
    const current = this.texts$.getValue();

    const updatedTexts = current.map((txt) => {
      if (txt.name === name) {
        return {
          ...txt,
          text,
        };
      }
      return txt;
    });

    this.texts$.next(updatedTexts);
  }

  public updateTextPosition({
    name,
    x,
    y,
  }: {
    name: string;
    x: number;
    y: number;
  }): void {
    const current = this.texts$.getValue();

    const updatedTexts = current.map((txt) => {
      if (txt.name === name) {
        return {
          ...txt,
          x: Math.round(x),
          y: Math.round(y),
        };
      }
      return txt;
    });

    this.texts$.next(updatedTexts);
  }

  public getTextByName(name: string): TextConfig {
    return this.texts$.getValue().find((line) => line.name === name);
  }

  public deleteText(idToDelete: string): void {
    const current = this.texts$.getValue();
    const updated = current.filter((text) => text.name !== idToDelete);
    this.texts$.next(updated);
  }

  public removeElement(): void {
    const elementToRemove = this.selectedElement$.getValue();

    // Если элемент не выбран, ничего не делаем
    if (!elementToRemove || !elementToRemove.name) {
      return;
    }

    switch (elementToRemove.type) {
      case typeConfigKonva.RECT:
        this.deleteRectangle(elementToRemove.name);
        break;
      case typeConfigKonva.LINE:
        this.deleteLine(elementToRemove.name);
        break;
      case typeConfigKonva.ARROW:
        this.deleteArrow(elementToRemove.name);
        break;
      case typeConfigKonva.TEXT:
        this.deleteText(elementToRemove.name);
        break;
      default:
        console.warn(
          `Unknown element type for removal: ${elementToRemove.type}`
        );
        return;
    }

    // Сбрасываем выделение после успешного удаления
    this.selectedElement$.next(null);
  }

  public allClear(): void {
    this.selectedElement$.next(null);
    this.rectangles$.next([]);
    this.lines$.next([]);
    this.arrows$.next([]);
    this.texts$.next([]);
  }
}
